import { useEffect } from 'react'

interface DocumentMeta {
  title: string
  description: string
  path?: string
}

/**
 * Sets document title, meta description, Open Graph tags, Twitter Card tags,
 * and canonical URL for SEO and social sharing.
 *
 * Call this hook at the top of any page component.
 */
export function useDocumentMeta({ title, description, path = '' }: DocumentMeta) {
  useEffect(() => {
    // Set page title
    document.title = title

    const setMeta = (
      selector: string,
      attrName: string,
      attrValue: string,
      content: string,
    ) => {
      let tag = document.querySelector(selector) as HTMLMetaElement | null
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute(attrName, attrValue)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    }

    const setLink = (rel: string, href: string) => {
      let tag = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
      if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', rel)
        document.head.appendChild(tag)
      }
      tag.setAttribute('href', href)
    }

    const canonicalUrl = `https://vcacapitaladvisors.com${path}`

    // Standard meta description (the snippet shown under Google results)
    setMeta('meta[name="description"]', 'name', 'description', description)

    // Open Graph tags (Facebook, LinkedIn, iMessage previews)
    setMeta('meta[property="og:title"]', 'property', 'og:title', title)
    setMeta('meta[property="og:description"]', 'property', 'og:description', description)
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl)
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'VCA Capital Advisors')

    // Twitter Card tags
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)

    // Canonical URL — tells Google this is the authoritative URL for this page
    setLink('canonical', canonicalUrl)
  }, [title, description, path])
}
